/**
 * The scenery a viewer sees now: their choice, their clock, the organization's settings.
 *
 * Re-evaluated every minute, so a room left open on a screen goes from afternoon to dusk on its own.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import {
  previewDate, sceneryFor, storeChoice, storedChoice, storedImage, storeImage,
  type Scenery, type SceneryChoice,
} from './scenery'

export interface RoomScenery {
  scenery: Scenery
  choice: SceneryChoice
  setChoice: (choice: SceneryChoice) => void
  hasOrgImage: boolean
  hasMyImage: boolean
  /** Keeps (or with null, forgets) the viewer's own image. False when the browser would not store it. */
  setMyImage: (dataUrl: string | null) => boolean
  /** The team's board over the Features e SDD area (docs/refactor/25), as a data URL; null shows the neon sign. */
  board: string | null
  /** Who put it up. */
  boardBy: string | null
  /** Puts up a new board (a picture or a GIF); what goes wrong comes back as the error. */
  setBoard: (file: File) => Promise<void>
  /** Takes the board down: the neon sign is back. */
  removeBoard: () => Promise<void>
}

function timeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

export const RoomSceneryContext = createContext<RoomScenery | null>(null)

/** The viewer's choice and how to change it, from the provider. Null outside it. */
export function useSceneryChoice(): RoomScenery | null {
  return useContext(RoomSceneryContext)
}

export function useRoomScenery(): RoomScenery {
  const [choice, setChoiceState] = useState<SceneryChoice>(storedChoice)
  const [mine, setMine] = useState<string | null>(storedImage)
  const [now, setNow] = useState<Date>(() => previewDate() ?? new Date())

  useEffect(() => {
    const id = window.setInterval(() => setNow(previewDate() ?? new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const { data: settings } = useQuery({ queryKey: ['room', 'scenery'], queryFn: api.room.scenery, staleTime: 5 * 60_000 })
  const version = settings?.imageVersion ?? null

  // Keyed by version, so a new image is fetched once and an unchanged one never again.
  const { data: orgImage } = useQuery({
    queryKey: ['room', 'background', version],
    queryFn: api.room.background,
    enabled: version != null,
    staleTime: Infinity,
    gcTime: Infinity,
  })

  // The team's board: fetched once per version, like the background.
  const queryClient = useQueryClient()
  const boardVersion = settings?.boardVersion ?? null
  const { data: boardImage } = useQuery({
    queryKey: ['room', 'board', boardVersion],
    queryFn: api.room.board,
    enabled: boardVersion != null,
    staleTime: Infinity,
    gcTime: Infinity,
  })
  const setBoard = useCallback(async (file: File) => {
    const next = await api.room.setBoard(file)
    queryClient.setQueryData(['room', 'scenery'], next)
  }, [queryClient])
  const removeBoard = useCallback(async () => {
    await api.room.removeBoard()
    await queryClient.invalidateQueries({ queryKey: ['room', 'scenery'] })
  }, [queryClient])

  const setChoice = useCallback((next: SceneryChoice) => {
    setChoiceState(next)
    storeChoice(next)
  }, [])

  const setMyImage = useCallback((dataUrl: string | null) => {
    const stored = storeImage(dataUrl)
    setMine(dataUrl)
    return stored
  }, [])

  const org = version != null ? orgImage ?? null : null

  // A choice whose image is gone falls back to following the clock.
  const effective: SceneryChoice =
    (choice === 'org-image' && !org) || (choice === 'my-image' && !mine) ? 'auto' : choice

  const scenery = useMemo(
    () => sceneryFor(effective, now, timeZone(), { org, mine }, settings?.decorations ?? true),
    [effective, now, org, mine, settings?.decorations],
  )

  return {
    scenery, choice: effective, setChoice, hasOrgImage: Boolean(org), hasMyImage: Boolean(mine), setMyImage,
    board: boardVersion != null ? boardImage ?? null : null, boardBy: settings?.boardBy ?? null, setBoard, removeBoard,
  }
}
